import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../shared/components/Button';
import { ShieldCheck, Cpu, BarChart2, ArrowRight } from 'lucide-react';
import { useAuth } from '@/services/auth/AuthProvider';
import { useTranslation } from 'react-i18next';
import styles from './Home.module.css';

const Home = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, role, isPasswordRecovery } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    if (isLoading || !isAuthenticated) return;

    // Don't auto-redirect during password recovery
    if (isPasswordRecovery) return;

    if (role === 'admin') {
      navigate('/admin', { replace: true });
      return;
    }

    navigate('/dashboard', { replace: true });
  }, [isAuthenticated, isLoading, navigate, role, isPasswordRecovery]);

  const features = [
    {
      icon: <ShieldCheck size={32} className={styles.featureIcon} />,
      title: t('pages.home.featureHealthPass'),
      description: t('pages.home.featureHealthPassDesc'),
    },
    {
      icon: <Cpu size={32} className={styles.featureIcon} />,
      title: t('pages.home.featureAI'),
      description: t('pages.home.featureAIDesc'),
    },
    {
      icon: <BarChart2 size={32} className={styles.featureIcon} />,
      title: t('pages.home.featureRealTime'),
      description: t('pages.home.featureRealTimeDesc'),
    },
  ];

  return (
    <div className={styles.pageContainer}>
      <main className={styles.mainContent}>
        <section className={styles.hero}>
          <div className={styles.heroContent}>
            <h1 className={styles.heroTitle}>
              {t('pages.home.heroTitle')}
            </h1>
            <p className={styles.heroSubtitle}>
              {t('pages.home.heroSubtitle')}
            </p>
            <div className={styles.heroActions}>
              <Button size="lg" to="/login">
                {t('pages.home.login')} <ArrowRight size={20} />
              </Button>
              <Button variant="outline" size="lg" to="/register">
                {t('pages.home.register')}
              </Button>
            </div>
          </div>
          <div className={styles.heroImageContainer}>
            <img
              src="https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=2070&q=80"
              alt={t('pages.home.heroImageAlt')}
              className={styles.heroImage}
            />
            <div className={styles.heroImageOverlay} />
          </div>
        </section>

        <section className={styles.featuresSection}>
          <div className={styles.featuresGrid}>
            {features.map((feature, index) => (
              <div key={index} className={styles.featureCard}>
                {feature.icon}
                <h3 className={styles.featureTitle}>{feature.title}</h3>
                <p className={styles.featureDescription}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

export default Home;
